import React, { useState, useEffect } from 'react';
import { FiCheck, FiX, FiLoader, FiMapPin, FiUsers, FiFolder, FiTrash2, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { API_ENDPOINTS } from '../apiConfig';
import { toast } from 'react-toastify';

const ViewVacancies = () => {
  const [vacancies, setVacancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const role = localStorage.getItem('role');

  useEffect(() => {
    fetchVacancies();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [filter, itemsPerPage]);

  const fetchVacancies = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.GET_VACANCIES);
      const data = await response.json();
      setVacancies(data.data);
    } catch (error) {
      console.error('Error fetching vacancies');
      toast.error('Error fetching vacancies');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await fetch(`${API_ENDPOINTS.VACANCY_STATUS}/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });
      toast.success('Status updated successfully');
      fetchVacancies();
    } catch (error) {
      console.error('Error updating status');
      toast.error('Error updating status');
    }
  };

  const deleteVacancy = async (id) => {
    try {
      await fetch(`${API_ENDPOINTS.DELETE_VACANCY}/${id}`, {
        method: 'DELETE',
      });
      toast.success('Vacancy deleted successfully');
      fetchVacancies();
    } catch (error) {
      console.error('Error deleting vacancy');
      toast.error('Error deleting vacancy');
    }
  };

  const filteredVacancies = vacancies.filter(vacancy => {
    if (filter === 'all') return true;
    return vacancy.status === filter;
  });

  // Pagination calculations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredVacancies.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredVacancies.length / itemsPerPage);

  const PaginationControls = () => (
    <div className="mt-6 flex flex-col sm:flex-row items-center justify-between px-4 py-3 bg-white rounded-xl shadow-md">
      <div className="flex items-center mb-4 sm:mb-0">
        <span className="mr-2 text-sm text-gray-600">Items per page:</span>
        <select
          value={itemsPerPage}
          onChange={(e) => setItemsPerPage(Number(e.target.value))}
          className="border rounded-lg px-3 py-1 text-sm bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#080366]"
        >
          {[5, 10, 25, 100].map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
        <span className="ml-4 text-sm text-gray-600">
          Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredVacancies.length)} of {filteredVacancies.length}
        </span>
      </div>
      
      <div className="flex items-center space-x-2">
        <button
          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          className={`p-2 rounded-lg ${
            currentPage === 1
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-white text-gray-600 hover:bg-gray-100'
          }`}
        >
          <FiChevronLeft className="w-5 h-5" />
        </button>
        
        <div className="flex items-center space-x-1">
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(page => {
              const distance = Math.abs(page - currentPage);
              return distance === 0 || distance === 1 || page === 1 || page === totalPages;
            })
            .map((page, index, array) => {
              if (index > 0 && array[index - 1] !== page - 1) {
                return [
                  <span key={`ellipsis-${page}`} className="px-2 text-gray-400">...</span>,
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-8 h-8 rounded-lg ${
                      currentPage === page
                        ? 'bg-[#080366] text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {page}
                  </button>
                ];
              }
              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`w-8 h-8 rounded-lg ${
                    currentPage === page
                      ? 'bg-[#080366] text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {page}
                </button>
              );
            })}
        </div>

        <button
          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
          className={`p-2 rounded-lg ${
            currentPage === totalPages
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-white text-gray-600 hover:bg-gray-100'
          }`}
        >
          <FiChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <FiLoader className="w-12 h-12 animate-spin text-[#080366]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8 lg:p-12">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#080366]">
            View <span className="text-[#fbba00]">Vacancies</span>
          </h1>
          <p className="mt-2 text-gray-600">Manage and monitor all job positions</p>
        </div>

        {/* Filter Section */}
        <div className="bg-white rounded-xl shadow-md p-4 mb-8">
          <div className="flex flex-wrap gap-4 justify-center md:justify-start">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-all text-sm ${
                filter === 'all'
                  ? 'bg-[#080366] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              All Positions
            </button>
            <button
              onClick={() => setFilter('open')}
              className={`px-4 py-2 rounded-lg font-medium transition-all text-sm ${
                filter === 'open'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Open
            </button>
            <button
              onClick={() => setFilter('closed')}
              className={`px-4 py-2 rounded-lg font-medium transition-all text-sm ${
                filter === 'closed'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Closed
            </button>
          </div>
        </div>

        {/* Vacancies for Desktop */}
        <div className="hidden md:block bg-white rounded-xl shadow-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-100 border-b">
              <tr>
                <th className="p-4 text-left text-gray-600 font-medium">Unit/Project Site</th>
                <th className="p-4 text-left text-gray-600 font-medium">Profile</th>
                {/* <th className="p-4 text-left text-gray-600 font-medium">No. of Positions</th> */}
                <th className="p-4 text-left text-gray-600 font-medium">Program</th>
                <th className="p-4 text-left text-gray-600 font-medium">Status</th>
                {role === 'superadmin' && (
                  <th className="p-4 text-left text-gray-600 font-medium">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {currentItems.map((vacancy) => (
                <tr key={vacancy._id} className="border-b hover:bg-gray-50 transition-colors">
                  <td className="p-4 text-gray-600">
                    <div className="flex items-center capitalize">
                      <FiMapPin className="mr-2 text-gray-400" />
                      {vacancy.location}
                    </div>
                  </td>
                  <td className="p-4 font-semibold capitalize text-gray-800">{vacancy.position}</td>
                  {/* <td className="p-4 text-gray-600">
                    <div className="flex items-center">
                      <FiUsers className="mr-2 text-gray-400" />
                      {vacancy.numberOfPositions} Position{vacancy.numberOfPositions > 1 ? 's' : ''}
                    </div>
                  </td> */}
                  <td className="p-4 text-gray-600">
                    <div className="flex items-center">
                      <FiFolder className="mr-2 text-gray-400" />
                      {vacancy.program.name}
                    </div>
                  </td>
                  <td className="p-4">
                    <span
                      className={`px-4 py-1 rounded-full text-sm font-medium ${
                        vacancy.status === 'open'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {vacancy.status.charAt(0).toUpperCase() + vacancy.status.slice(1)}
                    </span>
                  </td>
                  {role === 'superadmin' && (
                    <td className="p-4 flex items-center space-x-2">
                      {vacancy.status === 'open' ? (
                        <button
                          onClick={() => updateStatus(vacancy._id, 'closed')}
                          className="flex items-center justify-center px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-300"
                        >
                          <FiX className="mr-2" />
                          Close
                        </button>
                      ) : (
                        <button
                          onClick={() => updateStatus(vacancy._id, 'open')}
                          className="flex items-center justify-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition duration-300"
                        >
                          <FiCheck className="mr-2" />
                          Open
                        </button>
                      )}
                      <button
                        onClick={() => deleteVacancy(vacancy._id)}
                        className="flex items-center justify-center px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition duration-300"
                      >
                        <FiTrash2 />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {/* Empty State for Desktop */}
          {filteredVacancies.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-400 text-lg">
                No vacancies found for the selected filter.
              </div>
            </div>
          )}
        </div>

        {/* Vacancies Cards for Mobile */}
        <div className="md:hidden space-y-4">
          {currentItems.map((vacancy) => (
            <div 
              key={vacancy._id} 
              className="bg-white rounded-xl shadow-md p-4 relative"
            >
              <div className="flex justify-between items-start mb-3">
                <h2 className="text-lg font-semibold capitalize text-gray-800">{vacancy.position}</h2>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-medium ${
                    vacancy.status === 'open'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}
                >
                  {vacancy.status.charAt(0).toUpperCase() + vacancy.status.slice(1)}
                </span>
              </div>

              <div className="space-y-2 mb-3">
                <div className="flex capitalize items-center text-gray-600">
                  <FiMapPin className="mr-2 text-gray-400" />
                  <span>{vacancy.location}</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <FiUsers className="mr-2 text-gray-400" />
                  <span>
                    {vacancy.numberOfPositions} Position{vacancy.numberOfPositions > 1 ? 's' : ''}
                  </span>
                </div>
                <div className="flex items-center text-gray-600">
  <FiFolder className="mr-2 text-gray-400" />
  <span>{vacancy.program.name}</span>
</div>
</div>

{role === 'superadmin' && (
  <div className="flex space-x-2 mt-4">
    {vacancy.status === 'open' ? (
      <button
        onClick={() => updateStatus(vacancy._id, 'closed')}
        className="flex-1 flex items-center justify-center px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-300"
      >
        <FiX className="mr-2" />
        Close
      </button>
    ) : (
      <button
        onClick={() => updateStatus(vacancy._id, 'open')}
        className="flex-1 flex items-center justify-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition duration-300"
      >
        <FiCheck className="mr-2" />
        Open
      </button>
    )}
    <button
      onClick={() => deleteVacancy(vacancy._id)}
      className="flex items-center justify-center px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition duration-300"
    >
      <FiTrash2 />
    </button>
  </div>
)}
</div>
))}

{/* Empty State for Mobile */}
{filteredVacancies.length === 0 && (
  <div className="text-center py-12 bg-white rounded-xl shadow-md">
    <div className="text-gray-400 text-lg">
      No vacancies found for the selected filter.
    </div>
  </div>
)}
</div>

{/* Pagination Controls */}
{filteredVacancies.length > 0 && <PaginationControls />}
</div>
</div>
);
};

export default ViewVacancies;